/**
 * Ranker weights state helpers — gibbs-ranker-weights-ui study.
 *
 * Reads / writes workspace-scoped ranker weights via the existing
 * `gibbs/preferences.js` layer. The default behavior (no override) is
 * identical to today: `rankCompounds(data, defaultWeights)`. The
 * override lets a user pick a different primary score or disable the
 * flag-count tiebreak per workspace.
 *
 * Workspace key resolution:
 *   - If a protein is loaded, key = its PDB id (uppercased).
 *   - Otherwise key = 'default'.
 *
 * @module gibbs/results/rankerWeightsState
 */

import { getPreference, savePreference } from '../preferences';
import { defaultWeights } from './compoundRanker';

const WEIGHTS_PREF_KEY = 'rankerWeights';

/**
 * Get the workspace key for a loaded protein. Used as the dict key into
 * the per-workspace overrides map.
 *
 * @param {object} [workspace] - BMaps workspace (App.Workspace).
 *   Optional; missing workspace returns 'default'.
 * @returns {string}
 */
export function getWorkspaceKey(workspace) {
    if (!workspace || typeof workspace.getLoadedProtein !== 'function') {
        return 'default';
    }
    const protein = workspace.getLoadedProtein();
    const pdb = protein && (protein.pdbId || protein.pdb_id || protein.id);
    if (!pdb) return 'default';
    return String(pdb).toUpperCase();
}

/**
 * Read the active weights for a workspace. Returns `defaultWeights`
 * when no per-workspace override exists.
 *
 * @param {object} [workspace]
 * @returns {import('./compoundRanker').RankerWeights}
 */
export function getActiveWeights(workspace) {
    const key = getWorkspaceKey(workspace);
    const overrides = getPreference(WEIGHTS_PREF_KEY, {}) || {};
    const override = overrides[key];
    if (!override || typeof override !== 'object') return defaultWeights;
    // Merge over defaults so a partial override (e.g., only
    // tiebreakByFlagCount set) keeps the rest from defaults.
    return {
        primaryScorePreference: Array.isArray(override.primaryScorePreference)
            ? override.primaryScorePreference
            : defaultWeights.primaryScorePreference,
        tiebreakByFlagCount: typeof override.tiebreakByFlagCount === 'boolean'
            ? override.tiebreakByFlagCount
            : defaultWeights.tiebreakByFlagCount,
    };
}

/**
 * Persist a weight override for a workspace. Saves the merged
 * per-workspace map back through `savePreference`.
 *
 * @param {object|null} workspace
 * @param {Partial<import('./compoundRanker').RankerWeights>} updates
 * @returns {Promise<{ success: boolean, error: string|null }>}
 */
export async function setActiveWeights(workspace, updates) {
    const key = getWorkspaceKey(workspace);
    const current = getPreference(WEIGHTS_PREF_KEY, {}) || {};
    const existing = current[key] || {};
    const next = {
        primaryScorePreference: Array.isArray(updates.primaryScorePreference)
            ? updates.primaryScorePreference
            : (existing.primaryScorePreference
                || defaultWeights.primaryScorePreference),
        tiebreakByFlagCount: typeof updates.tiebreakByFlagCount === 'boolean'
            ? updates.tiebreakByFlagCount
            : (typeof existing.tiebreakByFlagCount === 'boolean'
                ? existing.tiebreakByFlagCount
                : defaultWeights.tiebreakByFlagCount),
    };
    const merged = { ...current, [key]: next };
    return savePreference(WEIGHTS_PREF_KEY, merged);
}

/**
 * Clear a workspace's override (revert to defaultWeights).
 *
 * @param {object|null} workspace
 * @returns {Promise<{ success: boolean, error: string|null }>}
 */
export async function clearActiveWeights(workspace) {
    const key = getWorkspaceKey(workspace);
    const current = getPreference(WEIGHTS_PREF_KEY, {}) || {};
    if (!(key in current)) return { success: true, error: null };
    const next = { ...current };
    delete next[key];
    return savePreference(WEIGHTS_PREF_KEY, next);
}

/**
 * Check whether a workspace currently has a custom override (vs.
 * inheriting defaultWeights). Drives UI affordances like "Reset to
 * default".
 *
 * @param {object|null} workspace
 * @returns {boolean}
 */
export function hasActiveOverride(workspace) {
    const key = getWorkspaceKey(workspace);
    const overrides = getPreference(WEIGHTS_PREF_KEY, {}) || {};
    return Boolean(overrides[key]);
}
